<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎵 Sustained Note Question Demo</h1>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Configuration</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Target Note</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="note-input">Note to Hold:</label>
                            <input
                                id="note-input"
                                v-model="targetNote"
                                type="text"
                                placeholder="e.g., C5, D#5, Bb5"
                                class="text-input"
                            />
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Quick Note Select</h3>
                    <div class="property-group">
                        <div class="button-group">
                            <button
                                v-for="note in quickNotes"
                                :key="note"
                                @click="targetNote = note"
                                :class="{ active: targetNote === note }"
                                class="state-btn"
                            >
                                {{ note }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Hold Duration</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="duration-slider"
                                >Duration: {{ sustainDuration }}ms ({{ (sustainDuration / 1000).toFixed(1) }}s)</label
                            >
                            <input
                                id="duration-slider"
                                type="range"
                                v-model.number="sustainDuration"
                                min="500"
                                max="3000"
                                step="250"
                            />
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Pitch Tolerance</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="tolerance-slider"
                                >Tolerance: {{ toleranceCents }} cents</label
                            >
                            <input
                                id="tolerance-slider"
                                type="range"
                                v-model.number="toleranceCents"
                                min="10"
                                max="100"
                                step="5"
                            />
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Actions</h3>
                    <div class="property-group">
                        <button @click="resetQuestion" class="reset-btn">
                            🔄 Reset Question
                        </button>
                    </div>
                </div>
            </div>

            <!-- Right Panel: Preview -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Live Question</h2>
                <div class="preview-box">
                    <SustainedNoteQuestionView
                        :key="questionKey"
                        :data="questionData"
                        @emit-response="handleResponse"
                    />
                </div>

                <!-- Response Monitor -->
                <v-card v-if="lastResponse" class="response-monitor bg-surface-light">
                    <v-card-title class="text-h6">
                        📊 Last Response
                    </v-card-title>
                    <v-card-text>
                        <div class="response-display">
                            <div class="response-row">
                                <strong>Achieved:</strong>
                                <span :class="lastResponse.achieved ? 'text-success' : 'text-error'">
                                    {{ lastResponse.achieved ? '✅ Yes' : '❌ No' }}
                                </span>
                            </div>
                            <div class="response-row">
                                <strong>Target Note:</strong>
                                {{ lastResponse.targetNote }}
                            </div>
                            <div class="response-row">
                                <strong>Sustained Duration:</strong>
                                {{ lastResponse.sustainedDuration.toFixed(0) }}ms
                            </div>
                            <div class="response-row">
                                <strong>Avg Cents Off:</strong>
                                {{ lastResponse.avgCentsOff.toFixed(1) }} cents
                            </div>
                            <div class="response-row">
                                <strong>Attempts:</strong>
                                {{ lastResponse.attempts }}
                            </div>
                            <div class="response-row">
                                <strong>Performance Score:</strong>
                                {{ (lastResponse.performance * 100).toFixed(1) }}%
                            </div>
                            <div class="response-row">
                                <strong>Is Correct:</strong>
                                {{ lastResponse.isCorrect ? '✅' : '❌' }}
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Instructions -->
        <v-card class="instructions-section">
            <v-card-title class="text-h5 text-secondary">
                How to Use
            </v-card-title>
            <v-card-text>
                <ol class="usage-list">
                    <li>
                        <strong>Select a target note</strong> using the input field or quick select buttons
                    </li>
                    <li>
                        <strong>Set the hold duration</strong> (how long you must sustain the note)
                    </li>
                    <li>
                        <strong>Play the target note</strong> on your flute - the question auto-starts listening
                    </li>
                    <li>
                        <strong>Hold the note steady</strong> - watch the progress bar fill up
                    </li>
                    <li>
                        <strong>If you lose the note</strong>, the timer resets and the attempt counter increments
                    </li>
                    <li>
                        When you successfully hold the note for the full duration, the question completes and submits an answer
                    </li>
                    <li>
                        Check the "Last Response" panel to see the evaluation results
                    </li>
                </ol>
            </v-card-text>
        </v-card>

        <!-- Performance Scoring -->
        <v-card class="api-section">
            <v-card-title class="text-h5 text-secondary">
                Performance Scoring
            </v-card-title>
            <v-card-text>
                <p>The question evaluates your performance based on:</p>
                <ul class="api-list">
                    <li><strong>Pitch Accuracy:</strong> Penalty for average deviation > 5 cents (scales from 0 at 5¢ to -0.5 at 50¢)</li>
                    <li><strong>Number of Attempts:</strong> -10% per additional attempt (capped at -30%)</li>
                    <li><strong>Base Score:</strong> 1.0 for achieving the sustained duration</li>
                </ul>
                <p class="mt-4"><strong>Example Scores:</strong></p>
                <ul class="api-list">
                    <li>First try, 3 cents off: 1.0 (perfect)</li>
                    <li>First try, 15 cents off: ~0.89</li>
                    <li>Second try, 5 cents off: 0.9</li>
                    <li>Third try, 25 cents off: ~0.53</li>
                </ul>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import SustainedNoteQuestionView from '../questions/SustainedNoteQuestionView.vue';
import type { SustainedNoteAnswer } from '../questions/SustainedNoteQuestion';
import { SustainedNoteQuestion } from '../questions/SustainedNoteQuestion';

const targetNote = ref('D5');
const sustainDuration = ref(750);
const toleranceCents = ref(50);
const questionKey = ref(0);

const quickNotes = [
    'C4', 'C#4', 'D4', 'D#4', 'E4', 'F4', 'F#4', 'G4', 'G#4', 'A4', 'A#4', 'B4',
    'C5', 'C#5', 'D5', 'D#5', 'E5', 'F5', 'F#5', 'G5', 'G#5', 'A5', 'A#5', 'B5',
    'C6', 'C#6', 'D6'
];

const lastResponse = ref<(SustainedNoteAnswer & { performance: number; isCorrect: boolean }) | null>(null);

const questionData = computed(() => {
    return [
        {
            targetNote: targetNote.value,
            sustainDuration: sustainDuration.value,
            toleranceCents: toleranceCents.value,
        },
    ];
});

function handleResponse(response: any) {
    console.log('Question response:', response);

    // Create a temporary question instance to evaluate the answer
    const tempQuestion = new SustainedNoteQuestion(questionData.value);
    const evaluation = tempQuestion.evaluate(response.answer, response.timeSpent);

    lastResponse.value = {
        ...response.answer,
        performance: evaluation.performance,
        isCorrect: evaluation.isCorrect,
    };
}

function resetQuestion() {
    questionKey.value++;
    lastResponse.value = null;
}
</script>

<style scoped>
.debug-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 30px;
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.text-input {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 8px;
}

.text-input:focus {
    outline: none;
}

.button-group {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.state-btn {
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    text-align: center;
    font-weight: 500;
}

.state-btn:hover {
    opacity: 0.8;
}

.state-btn.active {
    font-weight: bold;
}

.reset-btn {
    width: 100%;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    color: white;
    background: #007bff;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.reset-btn:hover {
    background: #0056b3;
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    border-radius: 6px;
    min-height: 500px;
}

.response-monitor {
    margin-top: 20px;
}

.response-display {
    font-family: monospace;
    font-size: 14px;
}

.response-row {
    padding: 8px 12px;
    margin-bottom: 6px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.02);
    display: flex;
    align-items: center;
    gap: 10px;
}

.response-row strong {
    min-width: 180px;
    color: #666;
}

.text-success {
    color: #28a745;
    font-weight: 700;
}

.text-error {
    color: #dc3545;
    font-weight: 700;
}

.instructions-section {
    margin: 40px 0;
}

.usage-list {
    line-height: 1.8;
    padding-left: 20px;
}

.usage-list li {
    margin-bottom: 12px;
}

.api-section {
    margin-top: 40px;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
